import { developerWorld, hostHeaderName, isLocalName, parseAllowedNetworks } from "odyssey-shared";

/**
 * The gateway the game page talks to (P1.4-T2). The page presents its session token to that gateway in
 * `hello`, and posts the login form's identifier and password and the tab's refresh token to the gateway's
 * account endpoints, so the gateway comes from the build's configuration. A `?server=` link is honoured only
 * on a page served from the machine itself, where local development and the browser suite point the page
 * at a gateway of their own; on any other origin it would let a link carry a player's credentials to a host
 * of the link author's choosing. With neither, the dev server's case, the page talks to port 7000 of its own host,
 * or to the port `DEVELOPER_WORLDS` names for a developer world, so a link to `?world=sandbox` reaches the
 * developer map's own map server from the workstation and from the local network alike (ADR-080).
 */

const LOCAL_HOSTNAMES: ReadonlySet<string> = new Set(["localhost", "127.0.0.1"]);
const DEFAULT_GATEWAY_PORT = 7000;

/**
 * Reports whether a page is served from the machine itself.
 *
 * @param hostname The page's `location.hostname`.
 * @returns True for exactly `localhost` and `127.0.0.1`.
 */
export function isLocalPage(hostname: string): boolean {
  return LOCAL_HOSTNAMES.has(hostname);
}

/**
 * The networks a development page's address may lie in: the defaults the dev server answers on, loopback and the
 * private ranges.
 */
const DEVELOPMENT_NETWORKS = parseAllowedNetworks(undefined);

/**
 * Reports whether a page is a development page: one the dev server would serve at its host, by the test the dev
 * server's own gate makes of a request's `Host` (`isLocalName`). The page's test hooks (`window.game`,
 * `window.phaserGame`) and the diagnostic strip exist only on such a page, since they tell who is near; the production
 * page and hosted, served under domain names, are never one. Wider than `isLocalPage`, which stays the rule for what
 * could carry a player's credentials elsewhere.
 *
 * @param host The page's `location.host`, its name or address with an optional port.
 * @param machine The dev server machine's own name as `config.json` carries it; empty for none, as every build has it.
 *   A name with a dot in it is taken as none, so no `config.json` can make a page under a domain name a development
 *   page.
 * @returns True for `localhost`, a literal address in loopback or the private ranges, or the machine's name, bare or
 *   with `.local`, on any port; false for any other name or address, and for a host that is not one host and port.
 * @example
 * isDevelopmentPage("192.168.10.110:3000", ""); // true
 * isDevelopmentPage("play.odysseytales.net", "ve"); // false
 */
export function isDevelopmentPage(host: string, machine: string): boolean {
  const name = hostHeaderName(host);
  return name !== null && isLocalName(name, DEVELOPMENT_NETWORKS, machine.includes(".") ? "" : machine);
}

/**
 * The WebSocket URL of the gateway the page connects to and logs in through.
 *
 * @param hostname The page's `location.hostname`.
 * @param requested The page's `?server=` value, or null when it has none.
 * @param configured The gateway origin the build's `config.json` names, or an empty string when it names none.
 * @param worldId The world the page plays.
 * @returns `requested` on a local page; otherwise `/game` on the configured gateway, or, when none is
 *   configured, on the developer world's port of the page's own host, or on port 7000 of it for any other world.
 */
export function gatewayUrl(hostname: string, requested: string | null, configured: string, worldId: string): string {
  if (requested !== null && isLocalPage(hostname)) {
    return requested;
  }
  if (configured !== "") {
    return `${configured.replace(/\/+$/, "")}/game`;
  }
  return `ws://${hostname}:${developerWorld(worldId)?.port ?? DEFAULT_GATEWAY_PORT}/game`;
}
